import type { Copy } from 'utils/person';

// Every word onboarding v4 says, in one place. The tone is a care expert on
// the phone: "you", "we", their name once we have it; never "process" or "ID".

// TODO[ENG-3751]: no AI policy URL exists in the repo yet; James to supply it.
export const AI_POLICY_URL = 'https://example.invalid/TODO-ENG-3751-ai-policy';

// The screens, the open ask and the cards are Vez's 2.0 design, word for word.

export const WELCOME = {
  title: "You're in",
  body: "Over the next few minutes we'll ask about you and the person you look after, and start putting their care profile together. Say as much or as little as you like.",
  action: 'Continue',
};

export const CONSENT = {
  title: 'Before we start',
  body: 'You can tell us about your situation in your own words: typed, spoken, or as a photo of a letter. We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice. A person is always involved.',
  /** The body, a point at a time, so each has room to be read. */
  points: [
    {
      icon: 'message',
      text: 'You can tell us about your situation in your own words: typed, spoken, or as a photo of a letter.',
    },
    {
      icon: 'sparkles',
      text: 'We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice.',
    },
    { icon: 'hand-holding-heart', text: 'A person is always involved.' },
  ],
  noteLead: 'Read',
  noteLink: 'how we use AI',
  agree: "That's fine with me",
  decline: "I'd rather not",
};

/** Consent as Keni's first message, where no screens come before the chat. */
export const CONSENT_IN_CHAT = {
  ask: 'Hi, our care experts help people find, fund and understand care, from working out what you could claim to finding the right carers.\n\nBefore we start, so you know: we use AI to help our care experts read what you share, so you only tell us once. A real person always looks over it.',
  ai: {
    label: 'How we use AI',
    points: [
      'You can tell us about your situation in your own words: typed, spoken, or as a photo of a letter.',
      'We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice.',
      'A real person always looks over it.',
    ],
    noteLead: 'Read the full',
    noteLink: 'AI policy',
  },
};

/** Keni on the first screen; a stand-in for the brand audit's final drawing. */
export const KENI_IMAGE_SRC = '/vector/keni-still.svg';

/** The prompt for what they need help with, asked about apart where it is left out. */
export const HELP_WITH_PROMPT =
  'What you need help with, like money and benefits or legal things';

/** The open first ask, with who it is for left to what they say. */
export const OPENING = {
  ask: "Tell me who you're looking after and what's happening with their care right now. Use your own words, and I'll point you to the right help.",
  promptsTitle: 'You could tell us about',
  prompts: [
    "Who you care for, even if it's yourself",
    'What caring for them involves',
    HELP_WITH_PROMPT,
  ],
};

/** Asked once the summary is confirmed where what they said left out what they need help with. */
export const HELP_WITH = {
  ask: 'So our care experts know where to start, what would you most like help with?',
  /** Said in turn while it is read. */
  readingLines: ["Going through what you've shared", 'Updating the summary'],
  /** Said with the summary read back with it in. */
  saysAgain: "Thanks. Here's the updated summary.",
};

export const WHO = {
  ask: "Hi, I'm Keni. First, who are we helping today?",
  someoneElse: 'Someone I look after',
  myself: 'Myself',
  exploring: 'Just looking for now',
};

/** Said before the open ask: what they get for telling us. */
export const TALK_PREAMBLE: Copy = {
  self: "Tell us about your situation in your own words, and I'll tell you how we can help.",
  other:
    "Tell us about them and what looking after them involves, in your own words, and I'll tell you how we can help.",
};

export const TALK = {
  ask: "Talk or type, whichever is easier. There's no right way to do this, and you can stop and start as often as you like.",
  recordHint: {
    orb: 'Tap the orb to record, and tap it again to stop. Take your time.',
    button:
      'Tap the microphone to record, and tap it again to stop. Take your time.',
  },
};

/** The prompts under the open ask; a good voice note leaves fewer triage questions. */
export const TALK_PROMPTS = {
  title: 'You could tell us about',
  self: [
    'Where you live, and roughly how old you are',
    'Any health conditions, and what you need help with day to day',
    'Who helps you at the moment, if anyone',
    "What you'd most like help with, like money, care or legal things",
  ],
  other: [
    'Who they are to you, and their name',
    'Where they live, and roughly how old they are',
    'Any health conditions, and what they need help with day to day',
    "What you'd most like help with, like money, care or legal things",
  ],
};

export const READING = {
  /** Said in turn while what they sent is read. */
  lines: [
    "Going through what you've shared",
    'Taking it all in',
    'Working out the right help',
  ],
  cardTitle: 'Reading what you told us',
  firstItem: {
    voiceNote: 'Listening to your voice note',
    written: 'Reading what you wrote',
  },
  /** Said while the summary of what they told us is written. */
  writingUpLines: ['Writing up what we heard'],
  /** Said once the reading is done, before any questions it left. */
  thanks: 'Thanks, that really helps.',
  allTold: "You've told us everything we need to get started.",
};

export const QUESTIONS = {
  /** Said when they are asked before they talk to us. */
  intro: {
    self: 'A few quick questions first, so we can check what you could be entitled to.',
    other:
      'A few quick questions about them first, so we can check what they could be entitled to.',
  },
  /** Said before the questions the talk left unanswered, so they know how many. */
  oneLeft: {
    self: 'One more question, so we can check what you could get.',
    other: 'One more question, so we can check what {careRecipient} could get.',
  },
  someLeft: {
    self: 'A couple more questions, so we can check what you could get.',
    other:
      'A couple more questions, so we can check what {careRecipient} could get.',
  },
  /** Said after the summary is confirmed, before the questions the talk left. */
  afterSummary: 'Thank you. There are a few more things we need to know.',
  /** Said when an answer was given in their own words rather than a tap. */
  saidInOwnWords: 'Thanks.',
};

export const HEARD = {
  says: "Thank you for telling me all that. Here's what I've understood.",
  saysAgain: "Thanks for letting us know. Here's the updated summary.",
  title: 'Does this look right?',
  partialTitle: "Here's what we got so far",
  partialNote:
    "We couldn't make out everything you sent. A care expert can go through it with you.",
  ask: 'Does this look right?',
  yes: "Yes, that's right",
  notQuite: 'Not quite',
  whatsWrong: "What have we got wrong? Talk or type, and we'll put it right.",
  thanks:
    "Thanks, we'll put that right. You can check the details at the end too.",
  stillReading:
    "We're still writing up what you told us. We'll have it ready for you shortly.",
  /** Read with their own correction of the summary, so the facts follow it. */
  editedContext: 'The summary of what they told us, as they corrected it',
};

export const HELP = {
  says: "Here's how we can help.",
  title: 'How we can help',
  ask: 'Would you like help with any of this?',
  yes: 'Yes',
  notNow: 'Not right now',
  /** Names no benefit until the eligibility API works out which. */
  money: {
    self: "Money you might be entitled to, and we'll help you claim it.",
    other:
      "Money {careRecipient} might be entitled to, and we'll help you claim it.",
  },
  care: {
    self: 'Care at home. We can find carers near you and help you compare them.',
    other:
      'Care at home. We can find carers near {careRecipient} and help you compare them.',
  },
  expert: 'A care expert who knows your situation, to go through it with you.',
};

/** What we would need from them, ticked off as it is found. */
export const NEED = {
  says: "Here's what would help us get started.",
  title: "What we'd need",
  ask: 'Shall we make a start now?',
  yes: 'Yes',
  later: "I'll do it later",
  name: {
    self: 'Your full name, spelled as it is on your letters',
    other: '{Their} full name, spelled as it is on their letters',
  },
  birth: { self: 'Your date of birth', other: '{Their} date of birth' },
  niNumber: {
    self: 'Your National Insurance number',
    other: '{Their} National Insurance number',
  },
  gp: { self: 'Your GP', other: '{Their} GP' },
  hospital: {
    self: "Any hospital stays you've had in the last year",
    other: "Any hospital stays they've had in the last year",
  },
  phone: {
    self: 'A phone number, so a care expert can call you',
    other: 'A phone number, so a care expert can call you',
  },
};

export const GATHER = {
  ask: "Send whatever you have, in any order. Say it, type it or add a photo of a letter. Tap I'm done when you're finished.",
  done: "I'm done",
  later: "I'll send the rest later",
};

/** Said where the person cared for is under 18, as only adults are helped. */
export const UNDER_18 = {
  ask: {
    self: "We're sorry, we can only help with care for adults at the moment. Your GP or your local council can point you to the right support.",
    other:
      "We're sorry, we can only help with care for adults at the moment. For a child or young person, your local council's children's services is a good place to start.",
  },
  changeAge: 'Change the age',
  /** Said when the age is typed again but cannot be read, so a tap is needed. */
  tapAge: 'Thanks. Could you tap the age, so I get it right?',
};

/** A number to reach them on, typed rather than said. */
export const PHONE = {
  ask: "What's your phone number? It helps us keep in touch, especially if things change quickly.",
  later: 'Do this later',
  /** Said back when a typed number will not do, with the question left open. */
  problem: {
    short:
      'That number looks too short. Could you check it? A UK number usually has 11 digits.',
    long: 'That number looks too long. Could you check it? A UK number usually has 11 digits.',
    notUk: "That doesn't look like a UK number. It should start with 0 or +44.",
  },
};

export const START = {
  ask: 'Great, we have enough to get started. What would you like to do next?',
  bookCall: 'Book a call with a care expert',
  claim: {
    self: 'Checking what I could claim',
    other: 'Checking what {careRecipient} could claim',
  },
  care: 'Finding care at home',
  expert: 'Talking to a care expert',
  later: 'Do this later',
  thanks: "Great. Last thing: check what we've got, and you're all set.",
};

export const CONFIRM = {
  section: 'Check the details',
  title: { self: 'About you', other: 'About {careRecipient}' },
  description:
    "We've filled these in from what you told us. Change anything we got wrong.",
  nameLabel: 'Name',
  confirm: "That's right",
};

export const SECTION = {
  aboutYou: 'Tell us about you',
  aboutThem: 'Tell us about them',
  howWeHelp: 'How we can help',
};

/** What Keni says when what they said does not get through. */
export const STATES = {
  micBlocked:
    "I can't hear you yet. To talk to me, let KareHero use your microphone in your phone's settings. Or you can type instead.",
  notUnderstood:
    "Sorry, I couldn't make that out. Could you try again, or type it if that's easier?",
  tooShort:
    "Thanks. Could you tell me a bit more? Who you look after, what that involves, and what you'd like help with.",
  resumed:
    'Welcome back. We were just getting started on this. Shall we carry on?',
};
